/**
 * @file WebsiteForm.tsx
 * @description Form component for creating and editing websites with WordPress configuration.
 */

import React, { useState, useEffect } from "react";
import { Website, Client } from "../../types";
import {
  CreateWebsiteData,
} from "../../services/websiteService";

interface WebsiteFormProps {
  website?: Website | null;
  clients: Client[];
  defaultClientId?: string;
  onSubmit: (data: CreateWebsiteData) => void;
  onCancel: () => void;
  isInline?: boolean;
}

const WebsiteForm: React.FC<WebsiteFormProps> = ({
  website,
  clients,
  defaultClientId,
  onSubmit,
  onCancel,
  isInline = false,
}) => {
  const [formData, setFormData] = useState<CreateWebsiteData>({
    clientId: website?.clientId || defaultClientId || "",
    website: website?.website || "",
    contentType: website?.contentType || "",
    wpUsername: website?.wpUsername || "",
    wpAppPassword: website?.wpAppPassword || "",
    wpPostType: website?.wpPostType || "post",
    wpStatus: website?.wpStatus || "draft",
    wpCategories: website?.wpCategories || [],
  });

  useEffect(() => {
    if (website) {
      setFormData({
        clientId: website.clientId,
        website: website.website,
        contentType: website.contentType,
        wpUsername: website.wpUsername,
        wpAppPassword: website.wpAppPassword,
        wpPostType: website.wpPostType || "post",
        wpStatus: website.wpStatus || "draft",
        wpCategories: website.wpCategories || [],
      });
    }
  }, [website]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      formData.clientId &&
      formData.website.trim() &&
      formData.contentType.trim() &&
      formData.wpUsername.trim() &&
      formData.wpAppPassword.trim()
    ) {
      onSubmit({
        ...formData,
        website: formData.website.trim(),
        contentType: formData.contentType.trim(),
        wpUsername: formData.wpUsername.trim(),
        wpAppPassword: formData.wpAppPassword.trim(),
      });
    }
  };

  const handleCategoriesChange = (value: string) => {
    const categories = value
      .split(",")
      .map((id) => parseInt(id.trim(), 10))
      .filter((id) => !isNaN(id));
    setFormData({ ...formData, wpCategories: categories });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={`p-6 border rounded-lg border-gray-line bg-base-100 ${isInline ? "" : "mb-6"}`}
    >
      <h4 className="!text-2xl !font-medium text-primary mb-4 text-left">
        {website ? "Modifica sito web" : "Nuovo sito web"}
      </h4>
      <div className="flex flex-col gap-4">
        <div>
          <label htmlFor="website-client" className="block mb-2 text-sm font-medium text-left">
            Cliente <span className="text-error">*</span>
          </label>
          <select
            id="website-client"
            value={formData.clientId}
            onChange={(e) =>
              setFormData({ ...formData, clientId: e.target.value })
            }
            className="w-full select select-bordered"
            required
            disabled={!!website}
          >
            <option value="">Seleziona un cliente</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="website-url" className="block mb-2 text-sm font-medium text-left">
            URL sito web <span className="text-error">*</span>
          </label>
          <input
            id="website-url"
            type="url"
            value={formData.website}
            onChange={(e) =>
              setFormData({ ...formData, website: e.target.value })
            }
            className="w-full input input-bordered"
            placeholder="https://example.com"
            required
            autoFocus
          />
        </div>

        <div>
          <label
            htmlFor="website-content-type"
            className="block mb-2 text-sm font-medium text-left"
          >
            Tipo di contenuto <span className="text-error">*</span>
          </label>
          <input
            id="website-content-type"
            type="text"
            value={formData.contentType}
            onChange={(e) =>
              setFormData({ ...formData, contentType: e.target.value })
            }
            className="w-full input input-bordered"
            placeholder="es. medico-sanitario"
            required
          />
        </div>

        <div className="divider text-left">Configurazione WordPress</div>

        <div>
          <label
            htmlFor="website-wp-username"
            className="block mb-2 text-sm font-medium text-left"
          >
            Username WordPress <span className="text-error">*</span>
          </label>
          <input
            id="website-wp-username"
            type="text"
            value={formData.wpUsername}
            onChange={(e) =>
              setFormData({ ...formData, wpUsername: e.target.value })
            }
            className="w-full input input-bordered"
            placeholder="username"
            required
          />
        </div>

        <div>
          <label
            htmlFor="website-wp-password"
            className="block mb-2 text-sm font-medium text-left"
          >
            Application Password WordPress <span className="text-error">*</span>
          </label>
          <input
            id="website-wp-password"
            type="password"
            value={formData.wpAppPassword}
            onChange={(e) =>
              setFormData({ ...formData, wpAppPassword: e.target.value })
            }
            className="w-full input input-bordered"
            placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
            required
          />
          <p className="mt-1 text-xs text-gray text-left">
            Crea un Application Password in WordPress: Utenti → Il tuo profilo →
            Application Passwords
          </p>
        </div>

        <div>
          <label
            htmlFor="website-wp-post-type"
            className="block mb-2 text-sm font-medium text-left"
          >
            Post Type WordPress
          </label>
          <input
            id="website-wp-post-type"
            type="text"
            value={formData.wpPostType}
            onChange={(e) =>
              setFormData({ ...formData, wpPostType: e.target.value })
            }
            className="w-full input input-bordered"
            placeholder="post"
          />
          <p className="mt-1 text-xs text-gray text-left">
            Default: post. Puoi usare anche "page" o un custom post type.
          </p>
        </div>

        <div>
          <label
            htmlFor="website-wp-status"
            className="block mb-2 text-sm font-medium text-left"
          >
            Status iniziale post
          </label>
          <select
            id="website-wp-status"
            value={formData.wpStatus}
            onChange={(e) =>
              setFormData({ ...formData, wpStatus: e.target.value })
            }
            className="w-full select select-bordered"
          >
            <option value="draft">Draft</option>
            <option value="publish">Publish</option>
            <option value="pending">Pending</option>
            <option value="private">Private</option>
          </select>
        </div>

        <div>
          <label
            htmlFor="website-wp-categories"
            className="block mb-2 text-sm font-medium text-left"
          >
            ID categorie WordPress (opzionale)
          </label>
          <input
            id="website-wp-categories"
            type="text"
            value={formData.wpCategories?.join(", ") || ""}
            onChange={(e) => handleCategoriesChange(e.target.value)}
            className="w-full input input-bordered"
            placeholder="1, 5, 10"
          />
          <p className="mt-1 text-xs text-gray text-left">
            Separati da virgola (es. 1, 5, 10). Solo per post type "post".
          </p>
        </div>

        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 rounded-full btn btn-outline"
          >
            Annulla
          </button>
          <button
            type="submit"
            className="px-6 rounded-full btn btn-primary"
            disabled={
              !formData.clientId ||
              !formData.website.trim() ||
              !formData.contentType.trim() ||
              !formData.wpUsername.trim() ||
              !formData.wpAppPassword.trim()
            }
          >
            {website ? "Salva" : "Crea"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default WebsiteForm;
